// MIRRORED FILE - twin of src/utils/clientInvoiceProfit.js (same rules; the only difference is where the
// line number comes from: there `clientInvoiceLine.billedLine`, here `num(lineNumber) ?? index + 1`, whose
// client twin has no billedLine; pinned equal by the PAIRS parity block). Change one, change both.
// CRA's ModuleScopePlugin forbids importing the repo's src/, so the page keeps a copy.
//
// D119.14: the ONE owner of "what an invoice earned" - `invoiceProfit` gives the per-line rows and the
// totals (Sell, Cost, Margin, costed Sell, Margin %, the no-cost flags). `orderProgress` (the invoice header's strip and
// the Profitability tab, D183) takes its rows from here; the Margin-by-vessel report (server twin) reads the totals
// unchanged. Pure; missing is null.
//
// Rules (owner 2026-10-15, BUSINESS_RULES#client-invoice-profit):
//  1. Sell = everything billed minus everything the non-cancelled credit notes took (drafts count); it
//     can go negative. A credit naming a line (`sourceInvoiceLineNumber`) comes off that line; a credit
//     naming none (a % discount, a correction with no line picked) is spread over the invoice's lines by
//     each line's share of the billed total, in cents that add up EXACTLY to the credit. A row's selling
//     is its line net of that; the TOTAL never depends on where a credit could land.
//  2. A line with no cost (blank or 0) is left out of the margin AND the margin %; Sell still counts it.
//     Cost counts every line that has one. Margin % = margin / sale of the lines with a sale and a cost.
//  3. A note's line references belong to its FIRST linked invoice (that is how credit limits cap
//     them; one it names that the invoice lacks is spread over that invoice's own lines); its no-line
//     credit is split across its linked invoices by `invoiceTotal`. `creditedTotal` is what one invoice
//     took in all, so a zero-line invoice's credit can come off its header total.
//  4. Credit does not touch cost: a credit note does not hand back what USC paid its supplier.
//
// Credit notes are read in the server document's shape (`extension`, `linkedInvoices[].clientInvoiceId`).
import { num, round2 } from './invoiceLineMath';
import { billedQuantity, priceClientInvoiceLine } from './clientInvoiceLine';

export interface ProfitCreditNote {
  status?: string | null;
  linkedInvoices?: ReadonlyArray<{ clientInvoiceId?: unknown; invoiceTotal?: unknown }> | null;
  items?: ReadonlyArray<{ sourceInvoiceLineNumber?: unknown; extension?: unknown }> | null;
}

export interface ProfitRow {
  lineNumber: number;
  selling: number | null;
  /** null = no cost (blank or 0). */
  cost: number | null;
  /** null unless the line has both a selling and a cost. */
  margin: number | null;
  marginPct: number | null;
  markupPct: number | null;
  /** What credit notes took off this line. */
  credited: number;
}

export interface ProfitTotals {
  /** Everything billed minus everything credited (may be negative); null only when nothing is billed. */
  selling: number | null;
  /** Every line that has a cost. */
  cost: number | null;
  /** Lines with both a net sale and a cost only. */
  margin: number | null;
  costedSelling: number | null;
  marginPct: number | null;
  markupPct: number | null;
  /** Lines that still sell for money after credits but carry no cost. */
  uncostedLines: number;
  hasCostData: boolean;
}

export interface InvoiceProfit {
  rows: ProfitRow[];
  totals: ProfitTotals;
}

const toCents = (n: number): number => Math.round(n * 100);
// A share weight, not a money figure: an amount that is missing or negative carries no share.
const weightCents = (value: unknown): number => Math.max(0, toCents(num(value) ?? 0));

/**
 * Share `totalCents` out by weight in whole cents that add up to `totalCents` exactly
 * (largest remainder; ties by key, so the order of `entries` changes nothing).
 */
export function splitCents<K extends number | string>(totalCents: number, entries: ReadonlyArray<{ key: K; weight: number }>): Map<K, number> {
  const weightSum = entries.reduce((s, e) => s + e.weight, 0);
  const out = new Map<K, number>(entries.map((e) => [e.key, 0]));
  if (!(weightSum > 0) || totalCents <= 0) return out;
  const parts = entries.map((e, i) => {
    const exact = (totalCents * e.weight) / weightSum;
    return { key: e.key, i, whole: Math.floor(exact), frac: exact - Math.floor(exact) };
  });
  let left = totalCents - parts.reduce((s, p) => s + p.whole, 0);
  parts.sort((a, b) => b.frac - a.frac || (a.key < b.key ? -1 : a.key > b.key ? 1 : a.i - b.i));
  for (const p of parts) out.set(p.key, p.whole + (left-- > 0 ? 1 : 0));
  return out;
}

const lineNumberOf = (item: any, index: number): number => num(item?.lineNumber) ?? index + 1;

/**
 * Everything the non-cancelled notes took off ONE invoice, in cents: `cents` per line, and `totalCents`
 * (which also counts a credit that no priced line could take). Rule 3 lives here and only here.
 */
function allocate(
  invoiceId: unknown,
  items: ReadonlyArray<any> | null | undefined,
  creditNotes: ReadonlyArray<ProfitCreditNote> | null | undefined
): { cents: Map<number, number>; totalCents: number } {
  const lines = (Array.isArray(items) ? items : []).map((item, i) => ({
    key: lineNumberOf(item, i),
    weight: weightCents(item?.invoiceLineTotal),
  }));
  const known = new Set(lines.map((l) => l.key));
  const cents = new Map<number, number>();
  let spreadCents = 0;
  let totalCents = 0;

  for (const cn of Array.isArray(creditNotes) ? creditNotes : []) {
    if (cn?.status === 'cancelled') continue;
    const links: NonNullable<ProfitCreditNote['linkedInvoices']> = Array.isArray(cn?.linkedInvoices) ? cn.linkedInvoices! : [];
    const mine = links.findIndex((l) => String(l?.clientInvoiceId) === String(invoiceId));
    if (mine < 0) continue;
    let noLine = 0;
    for (const it of cn?.items || []) {
      const ext = num(it?.extension);
      if (ext === null) continue;
      const amount = toCents(Math.abs(ext));
      const ref = num(it?.sourceInvoiceLineNumber);
      if (ref === null) noLine += amount; // split across the links below
      else if (mine === 0) { // the first invoice owns the note's line references; a ref it lacks is spread over ITS lines
        totalCents += amount;
        if (known.has(ref)) cents.set(ref, (cents.get(ref) || 0) + amount);
        else spreadCents += amount;
      }
    }
    if (!noLine) continue;
    const weights = links.map((l) => weightCents(l?.invoiceTotal));
    const even = weights.every((w) => w === 0);
    const share = splitCents(noLine, links.map((l, i) => ({ key: String(l?.clientInvoiceId), weight: even ? 1 : weights[i] })));
    const mineShare = share.get(String(links[mine]?.clientInvoiceId)) || 0;
    spreadCents += mineShare;
    totalCents += mineShare;
  }

  splitCents(spreadCents, lines).forEach((c, key) => cents.set(key, (cents.get(key) || 0) + c));
  return { cents, totalCents };
}

/**
 * What non-cancelled credit notes took off each line of ONE invoice.
 * Returns lineNumber -> money (2dp, positive); lines nothing touched are absent.
 */
export function creditedByLineNumber(
  invoiceId: unknown,
  items: ReadonlyArray<any> | null | undefined,
  creditNotes: ReadonlyArray<ProfitCreditNote> | null | undefined
): Map<number, number> {
  const money = new Map<number, number>();
  allocate(invoiceId, items, creditNotes).cents.forEach((c, key) => { if (c > 0) money.set(key, c / 100); });
  return money;
}

/**
 * Everything the notes took off ONE invoice (2dp), including a credit no priced line could take - a
 * zero-line invoice's credit comes off its header total with this. Same rules as `creditedByLineNumber`.
 */
export function creditedTotal(
  invoiceId: unknown,
  items: ReadonlyArray<any> | null | undefined,
  creditNotes: ReadonlyArray<ProfitCreditNote> | null | undefined
): number {
  return allocate(invoiceId, items, creditNotes).totalCents / 100;
}

// D119.19 / D153.9: a line billed 0 or less shows its unit mark-up % (the line owner at quantity 0); its margin
// stays out of every total.
function noQuantityMarkup(item: any): number | null {
  if ((billedQuantity(item) ?? 0) > 0) return null;
  return priceClientInvoiceLine({ quantity: 0, quoteUnitPrice: item?.quoteUnitPrice, actualUnitCost: item?.actualUnitCost }).markupPercent;
}

/**
 * D183.1: the margin figures of ONE line from its selling and cost (a cost of 0 is no cost), the one place the
 * line formulas live. Rows read it here; the client's live order progress reads its twin. A figure that cannot
 * be worked out is null, never 0.
 */
export function lineMargin(selling: number | null, cost: number | null): { margin: number | null; marginPct: number | null; markupPct: number | null } {
  const hasCost = cost !== null && cost !== 0;
  const margin = selling !== null && hasCost ? round2(selling - cost!) : null;
  return {
    margin,
    marginPct: margin !== null && selling! > 0 ? round2((margin / selling!) * 100) : null,
    markupPct: margin !== null && cost! > 0 ? round2((margin / cost!) * 100) : null,
  };
}

/** One row per invoice line: selling net of credits, cost, margin. */
function profitRows(
  items: ReadonlyArray<any> | null | undefined,
  creditNotes: ReadonlyArray<ProfitCreditNote> | null | undefined,
  invoiceId: unknown
): ProfitRow[] {
  const list = Array.isArray(items) ? items : [];
  const credited = creditedByLineNumber(invoiceId, list, creditNotes);
  return list.map((item, i) => {
    const lineNumber = lineNumberOf(item, i);
    const gross = num(item?.invoiceLineTotal);
    const credit = credited.get(lineNumber) || 0;
    const selling = gross !== null && credit > 0 ? round2(gross - credit) : gross;
    const rawCost = num(item?.actualLineCost);
    const cost = rawCost === 0 ? null : rawCost;
    const { margin, marginPct, markupPct } = lineMargin(selling, cost);
    return {
      lineNumber,
      selling,
      cost,
      margin,
      marginPct,
      markupPct: margin !== null && cost! > 0 ? markupPct : noQuantityMarkup(item),
      credited: credit,
    };
  });
}

/** Sum of the figures that exist, to the cent; null when none does (missing is never 0). The report sums rows with it. */
export function sumOrNull(values: ReadonlyArray<number | null>): number | null {
  const seen = values.filter((v): v is number => v !== null);
  return seen.length ? round2(seen.reduce((s, v) => s + v, 0)) : null;
}

/**
 * What an invoice earned: the per-line `rows` (what `orderProgress` builds its table from) and the `totals` EVERY
 * reader takes as they are (the Margin-by-vessel report converts each field and adds up).
 *  - selling: everything billed (the lines' totals, or the typed `headerTotal` when there are no lines)
 *    MINUS everything the notes took (`creditedTotal`), so no credit is ever dropped, whatever the lines
 *    look like; may go negative. null only when nothing is billed at all.
 *  - cost: every line that has a cost, whether or not it has a selling price.
 *  - margin, costedSelling, markupPct's cost: only lines with both a net sale and a cost.
 *  - marginPct = margin / costedSelling; markupPct = margin / those lines' cost.
 *  - uncostedLines: lines that still sell for money after credits and have no cost.
 */
export function invoiceProfit(
  invoice: { items?: ReadonlyArray<any> | null; headerTotal?: unknown } | null | undefined,
  creditNotes: ReadonlyArray<ProfitCreditNote> | null | undefined,
  invoiceId: unknown
): InvoiceProfit {
  const list = Array.isArray(invoice?.items) ? invoice!.items! : [];
  const rows = profitRows(list, creditNotes, invoiceId);
  const billed = list.length === 0 ? num(invoice?.headerTotal) : sumOrNull(list.map((item) => num(item?.invoiceLineTotal)));
  const margined = rows.filter((r) => r.margin !== null);
  const margin = sumOrNull(margined.map((r) => r.margin));
  const costedSelling = sumOrNull(margined.map((r) => r.selling));
  const marginedCost = sumOrNull(margined.map((r) => r.cost));
  const cost = sumOrNull(rows.map((r) => r.cost));
  return {
    rows,
    totals: {
      selling: billed === null ? null : round2(billed - creditedTotal(invoiceId, list, creditNotes)),
      cost,
      margin,
      costedSelling,
      marginPct: margin !== null && costedSelling! > 0 ? round2((margin / costedSelling!) * 100) : null,
      markupPct: margin !== null && marginedCost! > 0 ? round2((margin / marginedCost!) * 100) : null,
      uncostedLines: rows.filter((r) => r.cost === null && r.selling !== null && r.selling !== 0).length,
      hasCostData: cost !== null,
    },
  };
}
